// @em/blocks — content-template variables: the single, shared registry + resolver used everywhere
// authored content can carry `{{variable}}` merge tokens (policy waivers, event-website content,
// invitation emails / texts, reminders, …). Kept dep-free (no marked / sanitizer) so it's cheap to
// import anywhere; the HTML renderer that needs those libs lives in ./renderHtml.
//
// Vocabulary: tokens are **camelCase**, named after the entity prop they stand for (`eventName`,
// `venueAddress`, `guestName`, `rsvpGroupName`, `today`, …). This registry is the single source of
// truth — extend it here and every consumer (incl. the editors' insert menu) gets the new variable.
//
// Back-compat: earlier content (and value maps) used snake_case (`event_name`, `guest_name`). The
// resolver NORMALIZES any token spelling (snake_case, or a different case) to its canonical camelCase
// key via TOKEN_ALIASES, so old stored waivers keep rendering unchanged.

import { formatInZone } from "../dateFormat";

export type VariableGroup = "event" | "venue" | "guest" | "system";

/** Section headings for the grouped insert menu. */
export const VARIABLE_GROUP_LABELS: Record<VariableGroup, string> = {
  event: "Event",
  venue: "Venue / location",
  guest: "Guest",
  system: "Other",
};

export interface ContentVariable {
  key: string;
  label: string;
  group: VariableGroup;
  /** Width (in `ch`) of the blank line shown when the value is missing. */
  blankChars: number;
  /** An image variable (signature / initials): render the image if present, else a sign line. */
  image?: boolean;
  /** Render a taller blank line (for a full signature). */
  tall?: boolean;
}

// The registry. Add variables here — do not scatter literals across the apps.
export const CONTENT_VARIABLES: ContentVariable[] = [
  // Event
  { key: "eventName", label: "Event name", group: "event", blankChars: 30 },
  { key: "eventDate", label: "Event date", group: "event", blankChars: 18 },
  { key: "eventTime", label: "Event time", group: "event", blankChars: 12 },
  { key: "eventEndDate", label: "End date", group: "event", blankChars: 18 },
  { key: "eventEndTime", label: "End time", group: "event", blankChars: 12 },
  { key: "hostName", label: "Host / hosted by", group: "event", blankChars: 26 },
  { key: "organizationName", label: "Organization name", group: "event", blankChars: 28 },
  // Venue / location
  { key: "venueName", label: "Venue name", group: "venue", blankChars: 28 },
  { key: "venueAddress", label: "Venue address", group: "venue", blankChars: 40 },
  { key: "venuePhone", label: "Venue phone", group: "venue", blankChars: 18 },
  { key: "venueEmail", label: "Venue email", group: "venue", blankChars: 26 },
  { key: "venueWebsite", label: "Venue website", group: "venue", blankChars: 26 },
  { key: "eventLocation", label: "Location (short)", group: "venue", blankChars: 30 },
  // Guest
  { key: "guestName", label: "Guest name", group: "guest", blankChars: 30 },
  { key: "guestFirstName", label: "Guest first name", group: "guest", blankChars: 18 },
  { key: "rsvpGroupName", label: "Party name (group, else first name)", group: "guest", blankChars: 26 },
  { key: "guestEmail", label: "Guest email", group: "guest", blankChars: 28 },
  { key: "guestPhone", label: "Guest phone", group: "guest", blankChars: 18 },
  { key: "signature", label: "Signature", group: "guest", blankChars: 40, image: true, tall: true },
  { key: "initials", label: "Initials", group: "guest", blankChars: 10, image: true },
  // System
  { key: "today", label: "Today's date", group: "system", blankChars: 18 },
];

export const CONTENT_VARIABLES_BY_KEY: Record<string, ContentVariable> = CONTENT_VARIABLES.reduce(
  (acc, v) => {
    acc[v.key] = v;
    return acc;
  },
  {} as Record<string, ContentVariable>
);

/**
 * Alias table — maps a LOWERCASED token spelling to its canonical camelCase key. Covers:
 *   • every canonical key's own lowercase form (so `{{EVENTNAME}}` / `{{eventname}}` resolve), and
 *   • the legacy snake_case spellings that older content / value maps used.
 * Built once; `normalizeToken` consults it after an exact (case-sensitive) canonical match.
 */
const SNAKE_ALIASES: Record<string, string> = {
  event_name: "eventName",
  event_date: "eventDate",
  event_time: "eventTime",
  event_end_date: "eventEndDate",
  event_end_time: "eventEndTime",
  hosted_by: "hostName",
  host_name: "hostName",
  organization_name: "organizationName",
  venue_name: "venueName",
  venue_address: "venueAddress",
  venue_phone: "venuePhone",
  venue_email: "venueEmail",
  venue_website: "venueWebsite",
  event_location: "eventLocation",
  guest_name: "guestName",
  guest_first_name: "guestFirstName",
  rsvp_group_name: "rsvpGroupName",
  guest_email: "guestEmail",
  guest_phone: "guestPhone",
};

export const TOKEN_ALIASES: Record<string, string> = (() => {
  const map: Record<string, string> = { ...SNAKE_ALIASES };
  for (const v of CONTENT_VARIABLES) {
    map[v.key.toLowerCase()] = v.key;
  }
  // `hostedBy` was an older camelCase spelling; keep it pointing at the canonical host token.
  map.hostedby = "hostName";
  return map;
})();

/** Normalize any token spelling (camelCase / snake_case / any case) to its canonical camelCase key. */
export const normalizeToken = (raw: string): string => {
  const key = (raw ?? "").trim();
  if (CONTENT_VARIABLES_BY_KEY[key]) {
    return key;
  }

  return TOKEN_ALIASES[key.toLowerCase()] ?? key;
};

/** Matches `{{ key }}` tokens (optional inner spaces). Accepts camelCase AND snake_case. */
export const TEMPLATE_TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/** The token string for a variable key, e.g. "{{guestName}}". */
export const variableToken = (key: string): string => `{{${key}}}`;

/** One entry for the grouped insert menu. */
export interface VariableMenuEntry {
  key: string;
  label: string;
  group: VariableGroup;
  groupLabel: string;
}

/** Menu-ready entries (with group + section label), optionally filtered to groups. */
export const variableMenuItems = (groups?: VariableGroup[]): VariableMenuEntry[] =>
  CONTENT_VARIABLES.filter((v) => !groups || groups.includes(v.group)).map((v) => ({
    key: v.key,
    label: v.label,
    group: v.group,
    groupLabel: VARIABLE_GROUP_LABELS[v.group],
  }));

// ── Context ──────────────────────────────────────────────────────────────────
// Loose shapes so callers pass whatever entity objects they have. Field names
// mirror the underlying props; resolution tolerates several common aliases.

export interface TemplateEvent {
  title?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  hostedBy?: string | null;
  [key: string]: unknown;
}

export interface TemplateVenue {
  name?: string | null;
  /** Pre-formatted primary address (the caller formats it). */
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  [key: string]: unknown;
}

export interface TemplateGuest {
  name?: string | null;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  /** The guest's RSVP-group label ("Damons Family"), when they belong to a named group. */
  rsvpGroupName?: string | null;
  /** Encoded signature/initials images (data URLs), when captured. */
  signatureData?: string | null;
  initialsData?: string | null;
  [key: string]: unknown;
}

export interface TemplateContext {
  event?: TemplateEvent | null;
  venue?: TemplateVenue | null;
  guest?: TemplateGuest | null;
  /** Overrides / supplies values the entities don't carry inline. */
  organizationName?: string | null;
  location?: string | null;
  /** "Now" for the `today` variable (defaults to the current date). */
  now?: Date;
}

/** A resolved variable: at most one of text/image is set; both null = missing. */
export interface ResolvedVariable {
  text: string | null;
  image: string | null;
}

const toDate = (value?: string | Date | null): Date | null => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value?: string | Date | null, timeZone?: string | null): string | null => {
  const date = toDate(value);

  if (!date) {
    return null;
  }

  // ⚠ A fixed locale and the event's zone (else UTC) — never the runtime's: a card or a waiver printed on the server and
  //   one printed in a browser must read the same (2026-10-03, suggested task "Fix runtime-locale dates").
  return formatInZone(date.toISOString(), timeZone, { year: "numeric", month: "long", day: "numeric" }) || null;
};

const formatTime = (value?: string | Date | null, timeZone?: string | null): string | null => {
  const date = toDate(value);

  if (!date) {
    return null;
  }

  return formatInZone(date.toISOString(), timeZone, { hour: "numeric", minute: "2-digit" }) || null;
};

const guestFullName = (guest?: TemplateGuest | null): string | null => {
  if (!guest) {
    return null;
  }

  const joined = [guest.firstName, guest.lastName].filter(Boolean).join(" ").trim();

  return guest.name || guest.fullName || joined || null;
};

/** Resolve one variable key (canonical or aliased) against the context. Unknown keys → empty. */
export const resolveVariable = (rawKey: string, ctx: TemplateContext): ResolvedVariable => {
  const empty: ResolvedVariable = { text: null, image: null };
  const key = normalizeToken(rawKey);
  const { event, venue, guest } = ctx;
  const tz = event?.timezone ?? null;

  switch (key) {
    case "eventName":
      return { text: event?.title ?? null, image: null };
    case "eventDate":
      return { text: formatDate(event?.startsAt, tz), image: null };
    case "eventTime":
      return { text: formatTime(event?.startsAt, tz), image: null };
    case "eventEndDate":
      return { text: formatDate(event?.endsAt, tz), image: null };
    case "eventEndTime":
      return { text: formatTime(event?.endsAt, tz), image: null };
    case "hostName":
      return { text: event?.hostedBy ?? null, image: null };
    case "organizationName":
      return { text: ctx.organizationName ?? event?.hostedBy ?? null, image: null };
    case "venueName":
      return { text: venue?.name ?? null, image: null };
    case "venueAddress":
      return { text: venue?.address ?? null, image: null };
    case "venuePhone":
      return { text: venue?.phone ?? null, image: null };
    case "venueEmail":
      return { text: venue?.email ?? null, image: null };
    case "venueWebsite":
      return { text: venue?.website ?? null, image: null };
    case "eventLocation":
      return { text: ctx.location ?? venue?.name ?? null, image: null };
    case "guestName":
      return { text: guestFullName(guest), image: null };
    case "guestFirstName":
      return { text: guest?.firstName ?? guestFullName(guest), image: null };
    case "rsvpGroupName":
      // Who the invitation is addressed to: the party's name, else the person's FIRST name — the server's own rule
      // (eventsmagic-api `guestNames`), so a client render never reads differently from the sent message.
      return { text: guest?.rsvpGroupName?.trim() || guest?.firstName?.trim() || null, image: null };
    case "guestEmail":
      return { text: guest?.email ?? null, image: null };
    case "guestPhone":
      return { text: guest?.phone ?? null, image: null };
    case "signature":
      return { text: null, image: guest?.signatureData ?? null };
    case "initials":
      return { text: null, image: guest?.initialsData ?? null };
    case "today":
      // "Today" on the EVENT's calendar.
      return { text: formatDate(ctx.now ?? new Date(), ctx.event?.timezone ?? null), image: null };
    default:
      return empty;
  }
};

/**
 * Plain-text render — substitute tokens with values, or an underscore blank of a width that reflects
 * the variable. Image variables (signature/initials) become a blank line too (no image in text).
 * Unknown tokens are left as-is. For emails / SMS / any non-HTML surface.
 */
export const renderTemplateText = (body: string, ctx: TemplateContext): string => {
  if (!body) {
    return "";
  }

  return body.replace(TEMPLATE_TOKEN_RE, (match, rawKey: string) => {
    const key = normalizeToken(rawKey);
    const def = CONTENT_VARIABLES_BY_KEY[key];

    if (!def) {
      return match;
    }

    const { text } = resolveVariable(key, ctx);

    if (text) {
      return text;
    }

    return "_".repeat(def.blankChars);
  });
};
